import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Play } from 'lucide-react';
import MagneticButton from './MagneticButton';

/** Word-level reveal with blur+fade. Gradient classes apply cleanly. */
const LineReveal = ({ children, className = '', delay = 0 }) => (
  <span
    className={`inline-block animate-fadeInUp ${className}`}
    style={{ animationDelay: `${delay}ms`, animationDuration: '1.2s' }}
  >
    {children}
  </span>
);

const useCountUp = (end, duration = 2200, trigger = false) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const startTs = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, trigger]);
  return n;
};

const HeroStat = ({ value, label, inView, delay }) => {
  const match = /^(\d+)([^\d]*)$/.exec(value);
  const canAnim = !!match;
  const numeric = canAnim ? parseInt(match[1], 10) : 0;
  const suffix  = canAnim ? match[2] : '';
  const count = useCountUp(numeric, 2400, inView && canAnim);
  return (
    <div className="flex-1 animate-fadeInUp" style={{ animationDelay: `${delay}ms` }}>
      <div className="font-light text-4xl md:text-5xl chromatic-text tabular-nums tracking-tight">
        {canAnim ? `${count}${suffix}` : value}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
    </div>
  );
};

const Hero = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Parallax: scale + fade hero on scroll
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const parallax = Math.min(scrollY, 600);
  const heroOpacity = Math.max(1 - parallax / 600, 0);

  return (
    <section id="home" ref={sectionRef} className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden">
      {/* Chapter marker */}
      <div className="absolute top-28 left-6 lg:left-10 z-10 animate-fadeInUp" style={{ animationDelay: '200ms' }}>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/45">
          <span className="font-mono text-cyan-400">01 / 06</span>
          <span className="w-10 h-px bg-white/20" />
          <span>Chapter — The Signal</span>
        </div>
      </div>

      {/* Right meta panel */}
      <div className="hidden md:flex absolute top-28 right-6 lg:right-10 z-10 flex-col items-end gap-1 animate-fadeInUp" style={{ animationDelay: '300ms' }}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 font-mono">LAT 41.015 · LON 28.979</div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/70">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-blinkDot" />
          System online
        </div>
      </div>

      <div
        className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full"
        style={{ transform: `translateY(${parallax * 0.25}px) scale(${1 + parallax * 0.0003})`, opacity: heroOpacity }}
      >
        {/* Pretitle */}
        <div className="flex items-center gap-4 animate-fadeInUp" style={{ animationDelay: '400ms' }}>
          <span className="w-10 h-px bg-cyan-400" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-cyan-300/80">A software studio</span>
        </div>

        {/* Headline (word reveal) */}
        <h1 className="mt-8 font-light tracking-[-0.04em] leading-[0.88] text-[56px] sm:text-[96px] md:text-[140px] lg:text-[170px] xl:text-[190px]">
          <span className="block overflow-hidden">
            <LineReveal className="chromatic-text" delay={200}>We engineer</LineReveal>
          </span>
          <span className="block overflow-hidden">
            <LineReveal className="chromatic-text" delay={550}>the&nbsp;</LineReveal>
            <LineReveal className="font-serif neon-text italic" delay={800}>unseen.</LineReveal>
          </span>
        </h1>

        {/* Subhead + description row */}
        <div className="mt-10 grid md:grid-cols-12 gap-6 items-end">
          <p className="md:col-span-5 text-[15px] md:text-base text-white/55 leading-relaxed max-w-md animate-fadeInUp" style={{ animationDelay: '1400ms' }}>
            Ozzlabs is a quiet studio building artificial intelligence, interactive gaming worlds and invisible payment rails for the world&apos;s most ambitious products.
          </p>

          <div className="md:col-span-7 md:pl-6 md:border-l md:border-white/10 flex flex-col items-start gap-5 animate-fadeInUp" style={{ animationDelay: '1600ms' }}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Now playing</div>
            <div className="flex flex-wrap items-center gap-3">
              <MagneticButton>
                <a href="#services" data-cursor="hover" className="btn-primary inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full text-[13px] uppercase tracking-[0.22em]">
                  Enter the work
                  <span className="relative w-10 h-10 rounded-full bg-black text-white flex items-center justify-center overflow-hidden">
                    <ArrowUpRight className="w-4 h-4 relative z-10" />
                  </span>
                </a>
              </MagneticButton>
              <MagneticButton strength={0.2}>
                <a href="#contact" data-cursor="hover" className="btn-ghost inline-flex items-center gap-3 h-14 px-6 rounded-full text-[13px] uppercase tracking-[0.22em] text-white">
                  <Play className="w-4 h-4 fill-cyan-400 text-cyan-400" />
                  Showreel — 00:42
                </a>
              </MagneticButton>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-24 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-8 md:gap-4">
          <HeroStat value="500+" label="Missions shipped" inView={inView} delay={1800} />
          <HeroStat value="50+"  label="AI systems"     inView={inView} delay={1900} />
          <HeroStat value="100+" label="Live platforms" inView={inView} delay={2000} />
          <HeroStat value="24/7" label="On orbit"       inView={inView} delay={2100} />
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/35 animate-fadeInUp" style={{ animationDelay: '2200ms' }}>
        <span>Scroll · Scene 02</span>
        <div className="relative w-[1px] h-12 bg-white/10 overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-cyan-400 to-transparent animate-arrowSlide" style={{ animationDuration: '2s' }} />
        </div>
      </div>
    </section>
  );
};

export default Hero;
